import { motion } from "framer-motion";
import { useEffect, useState } from "react";

interface Firework {
  id: number;
  x: number;
  y: number;
  color: string;
}

interface Particle {
  id: number;
  angle: number;
  distance: number;
  color: string;
  size: number;
}

const Firework = ({ x, y, color }: { x: number; y: number; color: string }) => {
  const particles: Particle[] = Array.from({ length: 12 }, (_, i) => ({
    id: i,
    angle: (i * 30) * (Math.PI / 180),
    distance: 50 + Math.random() * 50,
    color,
    size: 4 + Math.random() * 4,
  }));

  return (
    <div 
      className="absolute pointer-events-none"
      style={{ left: `${x}%`, top: `${y}%` }}
    >
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full"
          style={{
            width: particle.size,
            height: particle.size,
            backgroundColor: particle.color,
            boxShadow: `0 0 10px ${particle.color}`,
          }}
          initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
          animate={{
            x: Math.cos(particle.angle) * particle.distance,
            y: Math.sin(particle.angle) * particle.distance,
            opacity: 0,
            scale: 0,
          }}
          transition={{
            duration: 0.8,
            ease: "easeOut",
          }}
        />
      ))}
      
      {/* Center burst */}
      <motion.div
        className="absolute w-4 h-4 rounded-full"
        style={{
          backgroundColor: color,
          boxShadow: `0 0 20px ${color}`,
          left: -8,
          top: -8,
        }}
        initial={{ scale: 0, opacity: 1 }}
        animate={{ scale: 3, opacity: 0 }}
        transition={{ duration: 0.5 }}
      />
    </div>
  );
};

const ValentineFireworks = () => {
  const [fireworks, setFireworks] = useState<Firework[]>([]);

  useEffect(() => {
    const colors = ["#ec4899", "#f43f5e", "#ef4444", "#a855f7", "#fbbf24"];
    let fireworkId = 0;

    const createFirework = () => {
      const newFirework: Firework = {
        id: fireworkId++,
        x: 10 + Math.random() * 80,
        y: 10 + Math.random() * 60,
        color: colors[Math.floor(Math.random() * colors.length)],
      };

      setFireworks(prev => [...prev, newFirework]);

      // Remove firework after animation
      setTimeout(() => {
        setFireworks(prev => prev.filter(f => f.id !== newFirework.id));
      }, 1000);
    };

    // Create initial burst
    for (let i = 0; i < 5; i++) {
      setTimeout(createFirework, i * 200);
    }

    // Continue creating fireworks
    const interval = setInterval(createFirework, 500);

    // Stop after 5 seconds
    setTimeout(() => clearInterval(interval), 5000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden z-40">
      {fireworks.map((firework) => (
        <Firework
          key={firework.id}
          x={firework.x}
          y={firework.y}
          color={firework.color}
        />
      ))}
    </div>
  );
};

export default ValentineFireworks;
